import React, { useMemo, useState } from 'react';
import { Button, Input, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import type { WorkflowInvoiceSourceLineCandidate } from '../../../types/workflowInvoice';
import { formatProductDimensions } from '../../../utils/productDimensions';
import { formatUnit } from '../../../utils/units';
import { rankSourceLines, type RankingOptions } from '../sourceLineRanking';

const VISIBLE_BY_DEFAULT = 3;
const SEARCH_FROM = 8;

interface SourceLinePickerProps {
  candidates: WorkflowInvoiceSourceLineCandidate[];
  /** Name printed on the invoice line; drives ordering and the "fits the invoice" hint. */
  invoiceName: string | null | undefined;
  /** Physical quantity already on the invoice line; a remainder equal to it ranks higher. */
  quantity?: RankingOptions['quantity'];
  /** Source line id -> position number of the other invoice line that already uses it. */
  takenBy?: Readonly<Record<string, number>>;
  value: string | null | undefined;
  onChange: (sourceOrderLineId: string | null) => void;
  disabled?: boolean;
  loading?: boolean;
  /** Shown for a locked line whose candidate list is not loaded. */
  fallbackLabel?: string | null;
  ariaLabel: string;
  testId: string;
}

function detailsOf(candidate: WorkflowInvoiceSourceLineCandidate): string[] {
  const dimensions = formatProductDimensions(candidate);
  const variant = candidate.variantLabel ?? null;
  const parts = [variant];
  // Variant labels often already spell the size; do not print it twice.
  if (dimensions && !(variant && variant.replace(/\s/g, '').includes(dimensions.replace(/\s|mm/g, '')))) {
    parts.push(dimensions);
  }
  return parts.filter((part): part is string => Boolean(part));
}

/**
 * Candidate source lines as radio cards. Each card carries what tells ZD lines
 * apart (name, variant, size, remainder) so nothing is hidden behind an ellipsis.
 */
const SourceLinePicker: React.FC<SourceLinePickerProps> = ({
  candidates, invoiceName, quantity, takenBy, value, onChange, disabled, loading, fallbackLabel, ariaLabel, testId,
}) => {
  const { t } = useTranslation('invoices');
  const [expanded, setExpanded] = useState(false);
  const [query, setQuery] = useState('');
  const taken = useMemo(() => new Set(Object.keys(takenBy ?? {})), [takenBy]);
  const { ranked, recommendedId } = useMemo(
    () => rankSourceLines(invoiceName, candidates, { quantity, takenIds: taken }),
    [invoiceName, candidates, quantity, taken],
  );

  const selected = candidates.find(candidate => candidate.sourceOrderLineId === value) ?? null;
  const needle = query.trim().toLowerCase();
  const matches = (candidate: WorkflowInvoiceSourceLineCandidate) => !needle
    || [candidate.orderNumber, candidate.productName, candidate.variantLabel, formatProductDimensions(candidate)]
      .some(part => (part ?? '').toLowerCase().includes(needle));

  let visible = ranked.map(entry => entry.candidate);
  if (disabled) {
    visible = selected ? [selected] : [];
  } else if (!expanded) {
    visible = visible.slice(0, VISIBLE_BY_DEFAULT);
    if (selected && !visible.includes(selected)) visible = [selected, ...visible.slice(0, VISIBLE_BY_DEFAULT - 1)];
  } else {
    visible = visible.filter(matches);
  }
  const hiddenCount = candidates.length - visible.length;

  if (disabled && !selected) {
    return fallbackLabel ? <div className="wie-source-name" data-testid={testId}>{fallbackLabel}</div> : null;
  }
  if (candidates.length === 0) {
    return (
      <Typography.Text type="secondary" data-testid={testId}>
        {t(loading ? 'workflow.page.loadingSources' : 'workflow.page.noSourceLines')}
      </Typography.Text>
    );
  }

  return (
    <div className="wie-source-picker" role="radiogroup" aria-label={ariaLabel} data-testid={testId}>
      {expanded && !disabled && candidates.length >= SEARCH_FROM && (
        <Input
          allowClear
          value={query}
          onChange={event => setQuery(event.target.value)}
          placeholder={t('workflow.sourcePicker.search')}
          aria-label={t('workflow.sourcePicker.search')}
        />
      )}
      {visible.map(candidate => {
        const checked = candidate.sourceOrderLineId === value;
        const details = detailsOf(candidate);
        const usedBy = !checked ? takenBy?.[candidate.sourceOrderLineId] : undefined;
        const remaining = candidate.remainingQuantity != null && candidate.remainingUnit
          ? t('workflow.sourcePicker.remaining', { quantity: `${candidate.remainingQuantity} ${formatUnit(candidate.remainingUnit)}` })
          : null;
        return (
          <button
            type="button"
            role="radio"
            aria-checked={checked}
            key={candidate.sourceOrderLineId}
            disabled={disabled}
            className={`wie-source-card${checked ? ' wie-source-card--on' : ''}`}
            data-testid={`workflow-source-option-${candidate.sourceOrderLineId}`}
            onClick={() => onChange(checked ? null : candidate.sourceOrderLineId)}
          >
            <span className="wie-source-dot" aria-hidden="true" />
            <span className="wie-source-body">
              <span className="wie-source-title">
                {candidate.productName}
                {details.length > 0 && <span className="wie-source-detail"> · {details.join(' · ')}</span>}
                {candidate.sourceOrderLineId === recommendedId && !checked && (
                  <span className="wie-source-badge">{t('workflow.sourcePicker.fits')}</span>
                )}
                {usedBy !== undefined && (
                  <span className="wie-source-badge wie-source-badge--used">{t('workflow.sourcePicker.usedBy', { number: usedBy })}</span>
                )}
              </span>
              <span className="wie-source-meta">{[candidate.orderNumber, remaining].filter(Boolean).join(' · ')}</span>
            </span>
          </button>
        );
      })}
      {!disabled && expanded && visible.length === 0 && (
        <Typography.Text type="secondary">{t('workflow.sourcePicker.noMatch')}</Typography.Text>
      )}
      {!disabled && !expanded && hiddenCount > 0 && (
        <Button type="link" size="small" className="wie-source-more" onClick={() => setExpanded(true)}>
          {t('workflow.sourcePicker.showMore', { count: hiddenCount })}
        </Button>
      )}
      {!disabled && expanded && candidates.length > VISIBLE_BY_DEFAULT && (
        <Button type="link" size="small" className="wie-source-more" onClick={() => { setExpanded(false); setQuery(''); }}>
          {t('workflow.sourcePicker.showLess')}
        </Button>
      )}
    </div>
  );
};

export default SourceLinePicker;
